<template>
  <b-modal
    :visible="visible"
    no-close-on-backdrop
    no-close-on-esc
    size="lg"
    @hide="close"
  >
    <template #modal-header>
      <div class="d-flex w-100 border-bottom pb-2">
        <div class="w-100 text-left">
          <h2 class="mb-1">Transporte e clientes</h2>
          <p class="mb-0 text-muted">
            Selecione os veículos disponíveis
            {{ slotDateLabel ? `em ${slotDateLabel}` : 'neste horário' }}.
          </p>
        </div>
        <button
          type="button"
          class="close"
          aria-label="Fechar"
          @click.prevent="close"
        >
          <span class="no no-e-remove"></span>
        </button>
      </div>
    </template>

    <p v-if="bookingCode || customerName" class="booking-vehicle-identity mb-3">
      <strong v-if="bookingCode">#{{ bookingCode }}</strong>
      <span v-if="customerName"> · {{ customerName }}</span>
      <span> · {{ peopleLabel }}</span>
    </p>

    <div v-if="!vehicles.length" class="text-center text-muted py-4">
      <p class="mb-2">
        Sem vagas disponíveis para {{ productName || 'esta atividade' }} em
        {{ slotDateLabel || 'este horário' }}.
      </p>
      <a
        v-if="showAvailabilityLink"
        href="#"
        class="btn btn-link p-0"
        @click.prevent="openAvailability"
      >
        abrir vagas
      </a>
    </div>

    <div v-else>
      <div
        v-for="group in vehiclesByCategory"
        :key="group.category"
        class="booking-vehicle-group"
      >
        <h4 class="booking-vehicle-category">{{ group.category }}</h4>
        <div
          v-for="vehicle in group.vehicles"
          :key="vehicle.uuid"
          class="booking-vehicle-row"
        >
          <b-form-checkbox
            :checked="isSelected(vehicle.uuid)"
            :disabled="
              !isSelected(vehicle.uuid) &&
              !dynamicCapacity(vehicle) &&
              seatsFor(vehicle) < 1
            "
            class="booking-vehicle-option"
            @change="toggleVehicle(vehicle, $event)"
          >
            <span class="font-weight-bold">{{ vehicle.name }}</span>
            <span class="text-muted">
              <template v-if="vehicle.plate"> · {{ vehicle.plate }}</template>
              <template v-if="!dynamicCapacity(vehicle)">
                · {{ vehicle.capacity }} pax ·
              </template>
            </span>
            <span
              v-if="!dynamicCapacity(vehicle)"
              :class="seatsFor(vehicle) < 1 ? 'text-danger' : 'text-muted'"
            >
              {{
                seatsFor(vehicle) < 1
                  ? 'lotado'
                  : `${seatsFor(vehicle)} vaga(s) livre(s)`
              }}
            </span>
          </b-form-checkbox>
          <div v-if="isSelected(vehicle.uuid)" class="booking-vehicle-pax">
            <input
              v-model.number="paxByUuid[vehicle.uuid]"
              type="number"
              min="1"
              :max="Math.min(totalPax, seatsFor(vehicle))"
              class="form-control form-control-sm"
            />
            <span class="text-muted">pessoas</span>
            <span
              v-if="
                !dynamicCapacity(vehicle) &&
                paxByUuid[vehicle.uuid] > seatsFor(vehicle)
              "
              class="text-danger"
            >
              só cabem {{ Math.max(seatsFor(vehicle), 0) }}
            </span>
          </div>
        </div>
      </div>

      <p v-if="selectedUuids.length" :class="['mb-0 mt-3', allocationClass]">
        Pessoas alocadas: <b>{{ allocatedPax }} de {{ totalPax }}</b>
        <span v-if="allocatedPax > totalPax">
          · mais do que a reserva tem
        </span>
        <span v-else-if="allocatedPax < totalPax">
          · {{ totalPax - allocatedPax }} a distribuir
        </span>
      </p>
    </div>

    <template #modal-footer>
      <base-button type="secondary" @click="close">Cancelar</base-button>
      <base-button
        type="primary"
        :disabled="saving || !canConfirm"
        @click="confirm"
      >
        {{ saving ? 'Salvando...' : 'Confirmar' }}
      </base-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'BookingVehicleModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    totalPax: {
      type: Number,
      default: 0,
    },
    // uuid -> vagas que esta reserva pode usar no carro (capacidade menos as
    // outras reservas). Sem a entrada, o limite é a capacidade do carro.
    seatsByUuid: {
      type: Object,
      default: () => ({}),
    },
    productName: {
      type: String,
      default: '',
    },
    bookingCode: {
      type: String,
      default: '',
    },
    customerName: {
      type: String,
      default: '',
    },
    slotDateLabel: {
      type: String,
      default: '',
    },
    showAvailabilityLink: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedUuids: [],
      paxByUuid: {},
    }
  },

  computed: {
    allocatedPax() {
      return this.selectedUuids.reduce(
        (sum, uuid) => sum + (Number(this.paxByUuid[uuid]) || 0),
        0
      )
    },

    allocationClass() {
      if (this.allocatedPax > this.totalPax) return 'text-danger'
      if (this.allocatedPax < this.totalPax) return 'booking-vehicle-warning'
      return 'text-muted'
    },

    peopleLabel() {
      const count = Number(this.totalPax) || 0
      return count === 1 ? '1 pessoa' : `${count} pessoas`
    },

    canConfirm() {
      return (
        this.selectedUuids.length > 0 &&
        this.allocatedPax <= this.totalPax &&
        this.selectedUuids.every((uuid) => {
          const pax = this.paxByUuid[uuid]
          const vehicle = this.vehicles.find((item) => item.uuid === uuid)
          return (
            Number.isInteger(pax) &&
            pax >= 1 &&
            (!vehicle ||
              this.dynamicCapacity(vehicle) ||
              pax <= this.seatsFor(vehicle))
          )
        })
      )
    },

    vehiclesByCategory() {
      const groups = []
      this.vehicles.forEach((vehicle) => {
        const category = vehicle.category || 'Veículo'
        let group = groups.find((item) => item.category === category)
        if (!group) {
          group = { category, vehicles: [] }
          groups.push(group)
        }
        group.vehicles.push(vehicle)
      })
      return groups
    },
  },

  watch: {
    visible(isOpen) {
      if (isOpen) this.syncSelection()
    },
    selected() {
      if (this.visible) this.syncSelection()
    },
  },

  methods: {
    // Um carro sem pax é legado: a reserva inteira está nele. Vários sem pax não
    // dizem a divisão, então o operador precisa preencher (ADR 001).
    syncSelection() {
      const selected = (this.selected || []).filter(
        (item) => item && (item.uuid || item.resource_uuid)
      )
      this.selectedUuids = selected.map((item) => item.uuid || item.resource_uuid)
      const paxByUuid = {}
      selected.forEach((item) => {
        const uuid = item.uuid || item.resource_uuid
        paxByUuid[uuid] =
          item.pax != null
            ? Number(item.pax)
            : selected.length === 1
            ? this.totalPax
            : null
      })
      this.paxByUuid = paxByUuid
    },

    dynamicCapacity(vehicle) {
      return Number(vehicle && vehicle.capacity) === 0
    },

    seatsFor(vehicle) {
      if (this.dynamicCapacity(vehicle)) return this.totalPax
      const seats = this.seatsByUuid[vehicle.uuid]
      return seats != null ? seats : Number(vehicle.capacity) || 0
    },

    isSelected(uuid) {
      return this.selectedUuids.includes(uuid)
    },

    toggleVehicle(vehicle, checked) {
      const uuid = vehicle.uuid
      if (checked) {
        if (!this.selectedUuids.includes(uuid)) {
          // Começa com quem ainda não tem carro, até as vagas livres do carro
          const remaining = Math.min(
            Math.max(this.totalPax - this.allocatedPax, 0),
            Math.max(this.seatsFor(vehicle), 0)
          )
          this.$set(this.paxByUuid, uuid, remaining || null)
          this.selectedUuids = this.selectedUuids.concat(uuid)
        }
        return
      }
      this.selectedUuids = this.selectedUuids.filter((item) => item !== uuid)
      this.$delete(this.paxByUuid, uuid)
    },

    confirm() {
      const vehicles = this.vehicles
        .filter((vehicle) => this.selectedUuids.includes(vehicle.uuid))
        .map((vehicle) => ({ ...vehicle, pax: this.paxByUuid[vehicle.uuid] }))
      this.$emit('confirm', vehicles)
    },

    close() {
      if (this.saving) return
      this.$emit('close')
    },

    openAvailability() {
      if (this.saving) return
      this.$emit('open-availability')
    },
  },
}
</script>

<style>
.booking-vehicle-identity {
  color: #525f7f;
}

.booking-vehicle-group + .booking-vehicle-group {
  margin-top: 1.25rem;
}

.booking-vehicle-category {
  margin-bottom: 0.5rem;
  color: #12576e;
}

.booking-vehicle-option {
  display: block;
  margin-bottom: 0.65rem;
}

/* O tema fixa height: 1rem no label do checkbox; texto em duas linhas vazava.
   Mesmo seletor do tema, prefixado, para ganhar na especificidade. */
.booking-vehicle-option.custom-checkbox
  .custom-control-input
  ~ .custom-control-label {
  height: auto;
}

.booking-vehicle-row {
  margin-bottom: 0.4rem;
}

.booking-vehicle-pax {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: -0.35rem 0 0.65rem 1.75rem;
  font-size: 0.85rem;
}

.booking-vehicle-pax input {
  width: 5rem;
}

.booking-vehicle-warning {
  color: #b56b12;
}
</style>
